import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  EspmPropertyTypeTypeahead,
  resolveEspmSelectionFromIds,
} from "../../components/Shared/espm";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { useEspmPropertyTypeTaxonomy } from "../../hooks/useEspmPropertyTypeTaxonomy";
import { Analytics } from "../../services/analytics/Analytics";
import { AccountAuth } from "../../services/auth/AccountAuth";
import { managesMultiplePortfolios } from "../../services/helpers/AccountClaimHelpers";
import { ICompanyClaims } from "../../services/zeos-api/contracts/Account";
import { IPropertyContract } from "../../services/zeos-api/contracts/Property";
import { IListItemSimple } from "../../services/zeos-api/contracts/ListUtility";
import {
  BUILDING_USE_LABELS,
  BUILDING_USE_OPTIONS,
  OWNER_CLASS_LABELS,
  OWNER_CLASS_OPTIONS,
  normalizeBuildingUses,
} from "../../services/incentiveMatchingVocabulary";
import {
  IScreeningApiReportFormat,
  IScreeningAgentTraceResponse,
  IScreeningRunHistoryItem,
  IScreeningRunStageResponse,
  IScreeningRunSummary,
} from "../../services/zeos-api/contracts/Screening";
import { ZeosListEnum, ZeosListOptionEnum } from "../../services/zeos-api/enums/ListUtility";
import { DashboardFooter } from "./layout/DashboardFooter";
import { DashboardHeader } from "./layout/DashboardHeader";

type ReportFormat = "pdf" | "docx";
type ScreeningMode = "quick" | "new" | "existing";
type PortfolioOption = Pick<ICompanyClaims, "companyId" | "companyName"> & {
  customerId?: number | null;
  customerName?: string | null;
};

type IntakeFormState = {
  address: string;
  companyName: string;
  utility: string;
  propertyType: string;
  espmPropertyTypeCategoryId?: number;
  espmPropertyTypeDetailId?: number;
  buildingUses: string[];
  ownerClass: string | null;
  isAffordableHousing: boolean | null;
  isHistoric: boolean | null;
  isPrimaryResidence: boolean | null;
  isBarn: boolean | null;
  isIncomeQualified: boolean | null;
  squareFeet: string;
  buildingAge: string;
  yearBuilt: string;
  lastRetrofitYear: string;
  economicObjectives: string;
  optionalCharacteristics: string;
};

type TextIntakeField =
  | "address"
  | "companyName"
  | "utility"
  | "propertyType"
  | "squareFeet"
  | "buildingAge"
  | "yearBuilt"
  | "lastRetrofitYear"
  | "economicObjectives"
  | "optionalCharacteristics";

const reportFormats: { value: ReportFormat; label: string }[] = [
  { value: "pdf", label: "Export PDF" },
  { value: "docx", label: "Export Word" },
];

const utilityOptionsByRegion = [
  {
    region: "New York",
    options: [
      "Central Hudson Gas & Electric",
      "Con Edison",
      "National Fuel Gas",
      "National Grid (NY)",
      "New York State Electric & Gas (NYSEG)",
      "Orange & Rockland",
      "PSEG Long Island",
      "Rochester Gas & Electric (RG&E)",
    ],
  },
  {
    region: "New Jersey",
    options: [
      "Atlantic City Electric",
      "Elizabethtown Gas",
      "Jersey Central Power & Light (JCP&L)",
      "New Jersey Natural Gas",
      "Orange & Rockland / Rockland Electric",
      "PSE&G",
      "South Jersey Gas",
    ],
  },
  {
    region: "California",
    options: [
      "Anaheim Public Utilities",
      "Imperial Irrigation District",
      "LADWP",
      "Pacific Gas & Electric (PG&E)",
      "Pasadena Water and Power",
      "Sacramento Municipal Utility District (SMUD)",
      "San Diego Gas & Electric (SDG&E)",
      "Silicon Valley Power",
      "Southern California Edison",
      "Southern California Gas (SoCalGas)",
    ],
  },
  {
    region: "Massachusetts",
    options: [
      "Berkshire Gas",
      "Eversource",
      "Liberty Utilities",
      "National Grid (MA)",
      "Unitil",
    ],
  },
];

const terminalRunStatuses = ["Completed", "Blocked", "Failed"];
export const RUN_POLL_INTERVAL_SECONDS = 6;
export const RUN_HISTORY_TAKE = 100;

export const screeningStageDefinitions = [
  { key: "IntakeExtraction", label: "Intake Extraction" },
  { key: "SkillSelection", label: "Skill Selection" },
  { key: "RuleEngine", label: "Rule Engine" },
  { key: "StatusGate", label: "Status Gate" },
  { key: "EligibilityVerification", label: "Eligibility Verification" },
  { key: "ValueStacking", label: "Value Stacking" },
  { key: "ReviewQueueSuggestions", label: "Review Queue Suggestions" },
  { key: "NarrativeReport", label: "Narrative Report" },
];

const emptyIntakeForm: IntakeFormState = {
  address: "",
  companyName: "",
  utility: "",
  propertyType: "",
  espmPropertyTypeCategoryId: undefined,
  espmPropertyTypeDetailId: undefined,
  buildingUses: [],
  ownerClass: null,
  isAffordableHousing: null,
  isHistoric: null,
  isPrimaryResidence: null,
  isBarn: null,
  isIncomeQualified: null,
  squareFeet: "",
  buildingAge: "",
  yearBuilt: "",
  lastRetrofitYear: "",
  economicObjectives: "",
  optionalCharacteristics: "",
};

export const toApiReportFormat = (format: ReportFormat): IScreeningApiReportFormat =>
  format === "pdf" ? "Pdf" : "Docx";

const demoScreeningProfiles: IntakeFormState[] = [
  {
    address: "315 Gates Avenue, Brooklyn, NY",
    companyName: "Gates Avenue Housing LLC",
    utility: "Con Edison",
    propertyType: "Multifamily Housing",
    espmPropertyTypeCategoryId: 15,
    espmPropertyTypeDetailId: 121,
    squareFeet: "30000",
    buildingAge: "96",
    yearBuilt: "1930",
    lastRetrofitYear: "2015",
    economicObjectives: "Lower operating costs and prioritize incentives with fast payback.",
    optionalCharacteristics: "Steam heat, rent-regulated units, roof access, planned boiler controls upgrade.",
  },
  {
    address: "118 West 129th Street, New York, NY",
    companyName: "Harlem Residential Partners",
    utility: "Con Edison",
    propertyType: "Multifamily Housing",
    espmPropertyTypeCategoryId: 15,
    espmPropertyTypeDetailId: 121,
    squareFeet: "42000",
    buildingAge: "117",
    yearBuilt: "1909",
    lastRetrofitYear: "2012",
    economicObjectives: "Reduce heating costs and identify electrification grants.",
    optionalCharacteristics: "Prewar walk-up, central steam, domestic hot water upgrade candidate.",
  },
  {
    address: "221 Canal Street, New York, NY",
    companyName: "Canal Street Mixed Use LLC",
    utility: "Con Edison",
    propertyType: "Retail",
    espmPropertyTypeCategoryId: 23,
    espmPropertyTypeDetailId: undefined,
    squareFeet: "45000",
    buildingAge: "104",
    yearBuilt: "1922",
    lastRetrofitYear: "2018",
    economicObjectives: "Maximize rebates while keeping tenant disruption low.",
    optionalCharacteristics: "Ground-floor retail, upper-floor residential, packaged rooftop units.",
  },
  {
    address: "44-02 11th Street, Long Island City, NY",
    companyName: "LIC Industrial Holdings",
    utility: "Con Edison",
    propertyType: "Warehouse/Storage",
    espmPropertyTypeCategoryId: 27,
    espmPropertyTypeDetailId: undefined,
    squareFeet: "68000",
    buildingAge: "82",
    yearBuilt: "1944",
    lastRetrofitYear: "2008",
    economicObjectives: "Find incentives for lighting, controls, and demand management.",
    optionalCharacteristics: "High-bay warehouse, loading docks, LED retrofit candidate, large roof area.",
  },
  {
    address: "30 Church Street, Rochester, NY",
    companyName: "Rochester Civic Office LLC",
    utility: "RG&E",
    propertyType: "Office",
    espmPropertyTypeCategoryId: 18,
    espmPropertyTypeDetailId: 130,
    squareFeet: "55000",
    buildingAge: "58",
    yearBuilt: "1968",
    lastRetrofitYear: "2016",
    economicObjectives: "Prioritize envelope and controls incentives.",
    optionalCharacteristics: "Municipal office tenant mix, older controls, weekday peak load profile.",
  },
  {
    address: "3900 Main Street, Riverside, CA",
    companyName: "Riverside Main Street Partners",
    utility: "Southern California Edison",
    propertyType: "Office",
    espmPropertyTypeCategoryId: 18,
    espmPropertyTypeDetailId: 130,
    squareFeet: "38000",
    buildingAge: "42",
    yearBuilt: "1984",
    lastRetrofitYear: "2019",
    economicObjectives: "Identify solar, storage, and efficiency incentives.",
    optionalCharacteristics: "Cooling-heavy load, surface parking, possible battery storage location.",
  },
  {
    address: "75 Varick Street, New York, NY",
    companyName: "Varick Commercial Owner LLC",
    utility: "Con Edison",
    propertyType: "Office",
    espmPropertyTypeCategoryId: 18,
    espmPropertyTypeDetailId: 130,
    squareFeet: "92000",
    buildingAge: "103",
    yearBuilt: "1923",
    lastRetrofitYear: "2014",
    economicObjectives: "Reduce peak demand and qualify controls measures.",
    optionalCharacteristics: "Central plant, tenant submetering, building management system in place.",
  },
  {
    address: "540 West 28th Street, New York, NY",
    companyName: "Chelsea Multifamily Owner LLC",
    utility: "Con Edison",
    propertyType: "Multifamily Housing",
    espmPropertyTypeCategoryId: 15,
    espmPropertyTypeDetailId: 121,
    squareFeet: "76000",
    buildingAge: "24",
    yearBuilt: "2002",
    lastRetrofitYear: "2020",
    economicObjectives: "Screen for Local Law 97-aligned incentives and heating upgrades.",
    optionalCharacteristics: "Elevator building, hydronic heating, garage ventilation, amenity spaces.",
  },
  {
    address: "160 Imlay Street, Brooklyn, NY",
    companyName: "Imlay Industrial Condo LLC",
    utility: "Con Edison",
    propertyType: "Manufacturing/Industrial",
    espmPropertyTypeCategoryId: 16,
    espmPropertyTypeDetailId: undefined,
    squareFeet: "110000",
    buildingAge: "113",
    yearBuilt: "1913",
    lastRetrofitYear: "2017",
    economicObjectives: "Identify retrofit incentives for adaptive reuse and electrification.",
    optionalCharacteristics: "Historic warehouse conversion, large windows, mixed residential and commercial load.",
  },
  {
    address: "1020 Grand Concourse, Bronx, NY",
    companyName: "Grand Concourse Housing Partners",
    utility: "Con Edison",
    propertyType: "Multifamily Housing",
    espmPropertyTypeCategoryId: 15,
    espmPropertyTypeDetailId: 121,
    squareFeet: "64000",
    buildingAge: "99",
    yearBuilt: "1927",
    lastRetrofitYear: "2011",
    economicObjectives: "Find utility rebates and state incentives for heating and common-area upgrades.",
    optionalCharacteristics: "Affordable housing, central boiler, common-area lighting, roof solar candidate.",
  },
].map((profile) => ({ ...emptyIntakeForm, ...profile }));

export const isTerminalRunStatus = (status?: string | null) =>
  terminalRunStatuses.some(
    (terminalStatus) => terminalStatus.toLowerCase() === (status || "").toLowerCase()
  );

export const normalizeStageKey = (value?: string | null) =>
  (value || "").replace(/[^a-z0-9]/gi, "").toLowerCase();

export const humanizeStageKey = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim() || value;

export const getChecklistStatusTone = (status?: string | null) => {
  const normalized = (status || "").toLowerCase();
  if (["completed", "complete", "succeeded", "success"].includes(normalized)) {
    return {
      dot: "bg-emerald-500",
      badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
      row: "border-emerald-200 bg-white",
    };
  }
  if (["blocked", "failed", "error"].includes(normalized)) {
    return {
      dot: "bg-red-500",
      badge: "border-red-200 bg-red-50 text-red-700",
      row: "border-red-200 bg-white",
    };
  }
  if (["running", "inprogress", "in progress", "processing"].includes(normalized)) {
    return {
      dot: "bg-zeos-orange-dk",
      badge: "border-orange-200 bg-orange-50 text-zeos-orange-dk",
      row: "border-orange-200 bg-orange-50/40",
    };
  }
  return {
    dot: "bg-slate-300",
    badge: "border-slate-200 bg-slate-50 text-slate-500",
    row: "border-slate-200 bg-white",
  };
};

export const parseOutputDetail = (outputJson?: string | null) => {
  if (!outputJson) return null;
  try {
    const parsed = JSON.parse(outputJson);
    if (typeof parsed === "string") {
      return parsed;
    }
    return parsed.summary || parsed.result || parsed.status || JSON.stringify(parsed, null, 2);
  } catch {
    return outputJson;
  }
};

export const parseStageReason = (stage: { outputJson?: string | null; errorMessage?: string | null }) => {
  if (stage.errorMessage) {
    return stage.errorMessage;
  }

  if (!stage.outputJson) {
    return null;
  }

  try {
    const parsed = JSON.parse(stage.outputJson);
    if (parsed.blockedByPreviousStage) {
      return "Prior step was blocked.";
    }
    return parsed.blockedReason || parsed.summary || null;
  } catch {
    return stage.outputJson;
  }
};

const parseRunIntakeForm = (run: IScreeningRunSummary): IntakeFormState => {
  try {
    const state = JSON.parse(run.jsonState || "{}");
    const intakeJson = resolveRunIntakeJson(state);
    return {
      address: intakeJson.address || "",
      companyName: intakeJson.companyName || "",
      utility: intakeJson.utility || "",
      propertyType: intakeJson.propertyType || "",
      espmPropertyTypeCategoryId:
        intakeJson.espmPropertyTypeCategoryId ?? undefined,
      espmPropertyTypeDetailId:
        intakeJson.espmPropertyTypeDetailId ?? undefined,
      buildingUses: normalizeBuildingUses(intakeJson.buildingUses),
      ownerClass:
        typeof intakeJson.ownerClass === "string" && intakeJson.ownerClass.trim()
          ? intakeJson.ownerClass
          : null,
      isAffordableHousing: readNullableBoolean(intakeJson.isAffordableHousing),
      isHistoric: readNullableBoolean(intakeJson.isHistoric),
      isPrimaryResidence: readNullableBoolean(intakeJson.isPrimaryResidence),
      isBarn: readNullableBoolean(intakeJson.isBarn),
      isIncomeQualified: readNullableBoolean(intakeJson.isIncomeQualified),
      squareFeet: intakeJson.squareFeet || "",
      buildingAge: intakeJson.buildingAge || intakeJson.age || "",
      yearBuilt: intakeJson.yearBuilt || "",
      lastRetrofitYear: intakeJson.lastRetrofitYear || intakeJson.yearOfLastRetrofit || "",
      economicObjectives: intakeJson.economicObjectives || "",
      optionalCharacteristics: intakeJson.optionalCharacteristics || "",
    };
  } catch {
    return emptyIntakeForm;
  }
};

const resolveRunIntakeJson = (state: any) =>
  state?.intakeSnapshot ||
  state?.runContext?.intakeSnapshot ||
  state?.intakeJson ||
  state?.intakeProfile?.enrichedContext?.previousStageContext?.intakeJson ||
  {};

const readNullableBoolean = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "1"].includes(normalized)) {
      return true;
    }
    if (["false", "no", "0"].includes(normalized)) {
      return false;
    }
  }
  return null;
};

const formatHistoryDate = (value?: string | null) => {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatPropertyAddress = (property?: IPropertyContract | null) =>
  [property?.address, property?.city, property?.state, property?.zip]
    .filter(Boolean)
    .join(", ");

const propertySearchText = (property: IPropertyContract) =>
  [
    property.propertyName,
    property.address,
    property.city,
    property.state,
    property.zip,
    property.bbl,
    property.bin,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export interface IScreeningCharacteristicsInput {
  units?: number | string | null;
  floors?: number | string | null;
  hvacLabels?: string[];
  roofSize?: number | string | null;
  bbl?: string | null;
  bin?: string | null;
  utilityIds?: number[];
}

// Single source of truth for the "optional characteristics" narrative phrase -- both the
// portfolio screening flow and the QA Screening Sandbox call this so an unedited property
// describes itself identically in both. HVAC is intentionally sourced from hvacPrimaryTypeIds
// (the structured, user-editable field), not the legacy free-text Property.hvac column, since
// that's the only HVAC value the Sandbox's edits can actually change.
export const buildScreeningCharacteristics = (input: IScreeningCharacteristicsInput) =>
  [
    input.units ? `${input.units} units` : null,
    input.floors ? `${input.floors} floors` : null,
    input.hvacLabels && input.hvacLabels.length > 0 ? `HVAC ${input.hvacLabels.join(", ")}` : null,
    input.roofSize ? `${input.roofSize} roof sq ft` : null,
    input.bbl ? `BBL ${input.bbl}` : null,
    input.bin ? `BIN ${input.bin}` : null,
    input.utilityIds?.length ? `Utility IDs ${input.utilityIds.join(", ")}` : null,
  ]
    .filter(Boolean)
    .join(", ");

const buildHydratedPropertyCharacteristics = (
  property: IPropertyContract,
  hvacOptions: IListItemSimple[]
) =>
  buildScreeningCharacteristics({
    units: property.units,
    floors: property.floors,
    hvacLabels: (property.hvacPrimaryTypeIds || [])
      .map((id) => hvacOptions.find((option) => option.dataKey === id)?.display)
      .filter((label): label is string => Boolean(label)),
    roofSize: property.roofSize,
    bbl: property.bbl,
    bin: property.bin,
    utilityIds: property.utilityIds,
  });

const mergePortfolioOptions = (options: PortfolioOption[]) =>
  Array.from(
    new Map(
      options
        .filter((company) => company.companyId && company.companyName)
        .map((company) => [company.companyId, company])
    ).values()
  ).sort((a, b) => a.companyName.localeCompare(b.companyName));

const parseUtilitySelection = (value: string) =>
  value
    .split(",")
    .map((utility) => utility.trim())
    .filter(Boolean);

export const getRunBlockedReason = (run: IScreeningRunSummary) => {
  const blockedStage = (run.stages || []).find(
    (stage) =>
      stage.status === "Blocked" && !stage.outputJson?.includes('"blockedByPreviousStage":true')
  );
  return blockedStage ? parseStageReason(blockedStage) : null;
};

export interface IScreeningUtilityHold {
  propertyId: number;
  reasonCode: string;
  reason: string;
  services: { serviceCode: string; status: string; candidates: string[]; message: string }[];
}

// UTL P07: a run held because the serving utilities aren't confirmed. The API writes the hold on
// the first stage as status "blocked" with a utilityHold object; null for any other run.
export const getRunUtilityHold = (run?: IScreeningRunSummary | null): IScreeningUtilityHold | null => {
  if (!run || run.status !== "Blocked") {
    return null;
  }
  for (const stage of run.stages || []) {
    if (stage.status !== "Blocked" || !stage.outputJson?.includes('"utilityHold"')) {
      continue;
    }
    try {
      const parsed = JSON.parse(stage.outputJson);
      if (!parsed.utilityHold) {
        continue;
      }
      return {
        propertyId: Number(parsed.utilityHold.propertyId ?? run.propertyId ?? 0),
        reasonCode: parsed.utilityHold.reasonCode ?? "",
        reason: parsed.blockedReason ?? "Screening is on hold until the serving utilities are confirmed.",
        services: parsed.utilityHold.services ?? [],
      };
    } catch {
      // A malformed stage is skipped; a later stage may still carry the hold.
      continue;
    }
  }
  return null;
};

export const findStageByKey = (stages: IScreeningRunStageResponse[], key: string) =>
  stages.find((stage) => normalizeStageKey(stage.stageKey) === normalizeStageKey(key));

export const findTraceByKey = (traces: IScreeningAgentTraceResponse[], key: string) =>
  [...traces]
    .reverse()
    .find((trace) => normalizeStageKey(trace.stageKey) === normalizeStageKey(key));

export const buildRunStatusMessage = (run: IScreeningRunSummary) => {
  const completedStages = (run.stages || []).filter((stage) => stage.status === "Completed").length;
  const totalStages = (run.stages || []).length;
  const latestTrace = (run.agentTraces || []).at(-1);
  const blockedReason = getRunBlockedReason(run);
  const stageText = totalStages > 0 ? `${completedStages}/${totalStages} stages complete` : "Waiting for stages";
  const traceText = latestTrace
    ? `Latest output: ${latestTrace.stageKey}`
    : "Waiting for first screening output";
  const blockedText = blockedReason ? ` Blocked reason: ${blockedReason}` : "";

  return `Screening ${run.status}; ${stageText}; current stage ${run.currentStage}. ${traceText}.${blockedText}`;
};

export interface IScreeningIntakeInput {
  source: string;
  mode: string;
  address: string;
  companyId?: number | null;
  companyName?: string | null;
  utility?: string | null;
  electricProvider?: string | null;
  gasProvider?: string | null;
  propertyType?: string | null;
  espmPropertyTypeCategoryId?: number | null;
  espmPropertyTypeDetailId?: number | null;
  // Matching facts. Answered rather than derived -- the engine reads these
  // directly and no longer infers owner class, use or historic status.
  buildingUses?: string[] | null;
  ownerClass?: string | null;
  isAffordableHousing?: boolean | null;
  isHistoric?: boolean | null;
  isPrimaryResidence?: boolean | null;
  isBarn?: boolean | null;
  isIncomeQualified?: boolean | null;
  squareFeet?: string | null;
  buildingAge?: string | null;
  yearBuilt?: string | null;
  lastRetrofitYear?: string | null;
  economicObjectives?: string | null;
  optionalCharacteristics?: string | null;
  propertyId?: number | null;
  propertySnapshot?: Record<string, unknown> | null;
}

// Single source of truth for the intakeText/intakeJson shape the screening engine reads --
// both the portfolio screening flow and the QA Screening Sandbox call this so their
// Screening/Runs requests can never drift apart again.
/**
 * A tri-state fact as prose. An explicit "No" is a real answer and must reach
 * the model -- a plain truthiness check would silently drop it and leave the
 * fact looking unanswered.
 */
const describeTriState = (label: string, value: boolean | null | undefined) =>
  value === null || value === undefined ? null : `${label}: ${value ? "Yes" : "No"}.`;

export const buildScreeningIntake = (input: IScreeningIntakeInput) => {
  const intakeJson = {
    source: input.source,
    mode: input.mode,
    address: input.address,
    companyName: input.companyName ?? null,
    utility: input.utility ?? null,
    electricProvider: input.electricProvider ?? null,
    gasProvider: input.gasProvider ?? null,
    propertyType: input.propertyType ?? null,
    espmPropertyTypeCategoryId: input.espmPropertyTypeCategoryId ?? null,
    espmPropertyTypeDetailId: input.espmPropertyTypeDetailId ?? null,
    buildingUses: normalizeBuildingUses(input.buildingUses ?? []),
    ownerClass: input.ownerClass ?? null,
    isAffordableHousing: input.isAffordableHousing ?? null,
    isHistoric: input.isHistoric ?? null,
    isPrimaryResidence: input.isPrimaryResidence ?? null,
    isBarn: input.isBarn ?? null,
    isIncomeQualified: input.isIncomeQualified ?? null,
    squareFeet: input.squareFeet ?? null,
    buildingAge: input.buildingAge ?? null,
    age: input.buildingAge ?? null,
    yearBuilt: input.yearBuilt ?? null,
    lastRetrofitYear: input.lastRetrofitYear ?? null,
    yearOfLastRetrofit: input.lastRetrofitYear ?? null,
    economicObjectives: input.economicObjectives ?? null,
    optionalCharacteristics: input.optionalCharacteristics ?? null,
    companyId: input.companyId ?? null,
    propertyId: input.propertyId ?? null,
    propertySnapshot: input.propertySnapshot ?? null,
  };

  const intakeText = [
    `Screening mode: ${input.mode}.`,
    `Location/address: ${input.address}.`,
    intakeJson.companyName ? `Company: ${intakeJson.companyName}.` : null,
    intakeJson.utility ? `Utility: ${intakeJson.utility}.` : null,
    intakeJson.propertyType ? `Property type: ${intakeJson.propertyType}.` : null,
    intakeJson.buildingUses.length
      ? `Building uses: ${intakeJson.buildingUses
          .map((use: string) => BUILDING_USE_LABELS[use as keyof typeof BUILDING_USE_LABELS] ?? use)
          .join(", ")}.`
      : null,
    intakeJson.ownerClass
      ? `Owner or applicant: ${OWNER_CLASS_LABELS[intakeJson.ownerClass as keyof typeof OWNER_CLASS_LABELS] ?? intakeJson.ownerClass}.`
      : null,
    describeTriState("Affordable housing", intakeJson.isAffordableHousing),
    describeTriState("Historic status", intakeJson.isHistoric),
    describeTriState("Income qualified", intakeJson.isIncomeQualified),
    intakeJson.squareFeet ? `Square feet: ${intakeJson.squareFeet}.` : null,
    intakeJson.buildingAge ? `Building age: ${intakeJson.buildingAge} years.` : null,
    intakeJson.yearBuilt ? `Year built: ${intakeJson.yearBuilt}.` : null,
    intakeJson.lastRetrofitYear ? `Last retrofit year: ${intakeJson.lastRetrofitYear}.` : null,
    intakeJson.economicObjectives
      ? `Economic objectives: ${intakeJson.economicObjectives}.`
      : null,
    intakeJson.optionalCharacteristics
      ? `Additional characteristics: ${intakeJson.optionalCharacteristics}.`
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  return { intakeText, intakeJson };
};

export const Screening = () => {
  const api = useContext(ZeosApiContext);
  const { categories: espmCategories, loading: espmLoading } =
    useEspmPropertyTypeTaxonomy();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPropertyId = Number(searchParams.get("property")) || null;
  const urlCompanyId = Number(searchParams.get("company")) || null;
  const urlRunGuid = searchParams.get("run");
  const showDemoTools = searchParams.has("demo");

  const [activeCompany, setActiveCompany] = useState<ICompanyClaims>();
  const [mode, setMode] = useState<ScreeningMode>(urlPropertyId ? "existing" : "quick");
  const [form, setForm] = useState<IntakeFormState>(emptyIntakeForm);
  const [availablePortfolios, setAvailablePortfolios] = useState<PortfolioOption[]>([]);
  const [currentZeosUserId, setCurrentZeosUserId] = useState<number | null>(null);
  const [canBrowseAllPortfolios, setCanBrowseAllPortfolios] = useState(false);
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(false);
  const [portfolioStatus, setPortfolioStatus] = useState<string | null>(null);
  const [selectedExistingCompanyId, setSelectedExistingCompanyId] = useState<number | null>(
    urlCompanyId
  );
  const [portfolioQuery, setPortfolioQuery] = useState("");
  const [propertyQuery, setPropertyQuery] = useState("");
  const [existingProperties, setExistingProperties] = useState<IPropertyContract[]>([]);
  const [hvacOptions, setHvacOptions] = useState<IListItemSimple[]>([]);
  const [selectedExistingPropertyDetails, setSelectedExistingPropertyDetails] =
    useState<IPropertyContract | null>(null);
  const [isLoadingExistingProperties, setIsLoadingExistingProperties] = useState(false);
  const [isLoadingExistingPropertyDetails, setIsLoadingExistingPropertyDetails] = useState(false);
  const [existingPropertyStatus, setExistingPropertyStatus] = useState<string | null>(null);
  const [selectedExistingPropertyId, setSelectedExistingPropertyId] = useState<number | null>(
    urlPropertyId
  );
  const [liveRun, setLiveRun] = useState<IScreeningRunSummary | null>(null);
  const [acknowledgedPreliminary, setAcknowledgedPreliminary] = useState(false);
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [pollCountdown, setPollCountdown] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [runHistory, setRunHistory] = useState<IScreeningRunHistoryItem[]>([]);
  const [historyStatus, setHistoryStatus] = useState<string | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [initializedRunGuid, setInitializedRunGuid] = useState<string | null>(null);
  const [isLoadingSavedRun, setIsLoadingSavedRun] = useState(false);
  const [initializedExistingCompanyId, setInitializedExistingCompanyId] = useState<number | null>(null);
  const [isDemoAddressPickerOpen, setIsDemoAddressPickerOpen] = useState(false);
  const [expandedStageKeys, setExpandedStageKeys] = useState<string[]>([]);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isPortfolioDropdownOpen, setIsPortfolioDropdownOpen] = useState(false);
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [isUtilityDropdownOpen, setIsUtilityDropdownOpen] = useState(false);

  const realRunId = liveRun?.runId ?? liveRun?.screeningRunId ?? null;
  const realRunGuid = liveRun?.runGuid ?? null;
  const selectedExistingCompany = useMemo(
    () =>
      availablePortfolios.find(
        (portfolio) => portfolio.companyId === selectedExistingCompanyId
      ) ?? null,
    [availablePortfolios, selectedExistingCompanyId]
  );
  const selectedUtilities = useMemo(
    () => parseUtilitySelection(form.utility),
    [form.utility]
  );
  const selectedExistingProperty = useMemo(
    () =>
      existingProperties.find(
        (property) => property.propertyId === selectedExistingPropertyId
      ) ?? null,
    [existingProperties, selectedExistingPropertyId]
  );
  const filteredPortfolios = useMemo(() => {
    const query = portfolioQuery.trim().toLowerCase();
    const list = query
      ? availablePortfolios.filter((portfolio) =>
          portfolio.companyName.toLowerCase().includes(query)
        )
      : availablePortfolios;
    return list.slice(0, 8);
  }, [availablePortfolios, portfolioQuery]);
  const filteredExistingProperties = useMemo(() => {
    const query = propertyQuery.trim().toLowerCase();
    const list = query
      ? existingProperties.filter((property) =>
          propertySearchText(property).includes(query)
        )
      : existingProperties;
    return list.slice(0, 10);
  }, [existingProperties, propertyQuery]);

  const activePropertyId =
    mode === "existing" ? selectedExistingPropertyId : liveRun?.propertyId ?? null;
  const resultTitle = `${form.address || selectedExistingProperty?.propertyName || "Property"} Screening Results`;
  const hasOutput =
    (liveRun?.stages || []).length > 0 ||
    (liveRun?.agentTraces || []).length > 0;
  const hasMeaningfulResults =
    Boolean(liveRun) &&
    liveRun?.status === "Completed" &&
    hasOutput;
  const utilityHold = getRunUtilityHold(liveRun);
  const canOpenIncentiveMatching =
    Boolean(realRunGuid) ||
    hasMeaningfulResults ||
    Boolean(activePropertyId);
  // Quick Screening stubs out the status gate, eligibility verification, and value
  // stacking stages, and carries no property record, so its output is a preliminary
  // candidate list rather than an eligibility determination. Require an explicit
  // acknowledgement before it can leave the app as a report.
  const isPreliminaryRun =
    Boolean(liveRun) && (liveRun?.screeningMode ?? "").toLowerCase() === "quick";
  const canExportReport =
    Boolean(realRunId) && (!isPreliminaryRun || acknowledgedPreliminary);

  useEffect(() => {
    setAcknowledgedPreliminary(false);
  }, [realRunId]);
  const runIdentity = realRunGuid ?? (realRunId ? realRunId.toString() : null);
  const agenticChecklist = useMemo(() => {
    const stages = liveRun?.stages || [];
    const traces = liveRun?.agentTraces || [];
    const knownKeys = screeningStageDefinitions.map((stage) => stage.key);
    const dynamicKeys = [...stages.map((stage) => stage.stageKey), ...traces.map((trace) => trace.stageKey)]
      .filter((key): key is string => Boolean(key))
      .filter(
        (key, index, keys) =>
          keys.findIndex((candidate) => normalizeStageKey(candidate) === normalizeStageKey(key)) === index
      )
      .filter(
        (key) =>
          !knownKeys.some((knownKey) => normalizeStageKey(knownKey) === normalizeStageKey(key))
      );
    const orderedKeys = [...knownKeys, ...dynamicKeys];

    return orderedKeys.map((key) => {
      const stage = findStageByKey(stages, key);
      const trace = findTraceByKey(traces, key);
      const definition = screeningStageDefinitions.find(
        (item) => normalizeStageKey(item.key) === normalizeStageKey(key)
      );
      const currentStageMatches =
        normalizeStageKey(liveRun?.currentStage) === normalizeStageKey(key);
      const status = stage?.status || (currentStageMatches ? "Running" : "Pending");
      const stageDetail = stage
        ? parseStageReason(stage) || parseOutputDetail(stage.outputJson)
        : null;
      const traceDetail = trace ? parseOutputDetail(trace.outputJson) : null;
      const detail = [stageDetail, traceDetail]
        .filter((item): item is string => Boolean(item))
        .filter((item, index, items) => items.indexOf(item) === index)
        .join("\n\n");

      return {
        key,
        label: definition?.label || humanizeStageKey(key),
        status,
        detail,
        hasDetail: Boolean(detail),
      };
    });
  }, [liveRun]);
  const allChecklistItemsExpanded =
    agenticChecklist.length > 0 &&
    agenticChecklist.every((item) => expandedStageKeys.includes(item.key));

  useEffect(() => {
    setExpandedStageKeys([]);
    setExportError(null);
  }, [runIdentity]);

  const applyLoadedRun = useCallback((run: IScreeningRunSummary, messagePrefix = "Loaded saved screening run") => {
    const parsedForm = parseRunIntakeForm(run);
    let loadedCompanyId: number | null = null;
    try {
      const state = JSON.parse(run.jsonState || "{}");
      const intakeJson = resolveRunIntakeJson(state);
      loadedCompanyId = Number(run.companyId ?? state?.companyId ?? state?.runContext?.companyId ?? intakeJson?.companyId) || null;
    } catch {
      loadedCompanyId = null;
    }
    const loadedPropertyId = run.propertyId ?? null;
    setLiveRun(run);
    setForm(parsedForm);
    setMode(loadedPropertyId ? "existing" : "quick");
    if (loadedCompanyId) {
      setSelectedExistingCompanyId(loadedCompanyId);
    }
    setSelectedExistingPropertyId(loadedPropertyId);
    setIsRunning(!isTerminalRunStatus(run.status));
    setPollCountdown(isTerminalRunStatus(run.status) ? 0 : RUN_POLL_INTERVAL_SECONDS);
    setRunStatus(`${messagePrefix}. ${buildRunStatusMessage(run)}`);
  }, []);

  const setRunGuidInUrl = useCallback((runGuid?: string | null) => {
    const nextParams = new URLSearchParams(searchParams);
    if (runGuid) {
      nextParams.set("run", runGuid);
    } else {
      nextParams.delete("run");
    }
    setSearchParams(nextParams, { replace: false });
  }, [searchParams, setSearchParams]);

  const loadRunByGuid = useCallback(async (runGuid: string, updateUrl = true) => {
    if (!runGuid) return;
    setIsLoadingSavedRun(true);
    setRunStatus("Loading saved screening run...");
    try {
      const response = await api.Screening.getRunByGuidAsync(runGuid);
      if (!response?.data) {
        throw new Error(response?.message || "Saved screening run was not found.");
      }
      applyLoadedRun(response.data);
      setInitializedRunGuid(runGuid);
      if (updateUrl) {
        setRunGuidInUrl(response.data.runGuid || runGuid);
      }
    } catch (error: any) {
      setRunStatus(error?.message || "Unable to load saved screening run.");
      setIsRunning(false);
      setPollCountdown(0);
    } finally {
      setIsLoadingSavedRun(false);
    }
  }, [api.Screening, applyLoadedRun, setRunGuidInUrl]);

  const loadRunHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    setHistoryStatus(null);
    try {
      const response = await api.Screening.getRunHistoryAsync({
        companyId: mode === "existing"
          ? selectedExistingCompanyId ?? activeCompany?.companyId ?? urlCompanyId ?? null
          : activeCompany?.companyId ?? urlCompanyId ?? null,
        propertyId: mode === "existing" ? selectedExistingPropertyId ?? urlPropertyId : null,
        serviceProviderId: 1,
        take: RUN_HISTORY_TAKE,
      });
      setRunHistory(response?.data || []);
    } catch (error: any) {
      setHistoryStatus(error?.message || "Unable to load run history.");
    } finally {
      setIsHistoryLoading(false);
    }
  }, [activeCompany?.companyId, api.Screening, mode, selectedExistingCompanyId, selectedExistingPropertyId, urlCompanyId, urlPropertyId]);

  useEffect(() => {
    if (urlRunGuid && initializedRunGuid !== urlRunGuid) {
      loadRunByGuid(urlRunGuid, false);
    }
  }, [initializedRunGuid, loadRunByGuid, urlRunGuid]);

  useEffect(() => {
    loadRunHistory();
  }, [loadRunHistory]);

  useEffect(() => {
    const loadHvacOptions = async () => {
      try {
        const response = await api.ListUtility.getListAsync(
          ZeosListEnum.HvacPrimaryType,
          ZeosListOptionEnum.excludeUtility
        );
        setHvacOptions(response.data ?? []);
      } catch {
        // Non-critical -- HVAC just won't appear in the characteristics narrative.
      }
    };
    loadHvacOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Screening is a Service Layer tool. Single-portfolio customers never see the nav
  // item (see DashboardHeader); if one reaches the route directly, send them back to
  // their portfolio rather than rendering tools that do not apply to their account.
  useEffect(() => {
    let isMounted = true;
    const guardAsync = async () => {
      const user = await AccountAuth.zeosUserAsync();
      if (isMounted && !managesMultiplePortfolios(user)) {
        navigate("/portfolio", { replace: true });
      }
    };
    guardAsync();
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;
    const loadPortfoliosAsync = async () => {
      setIsLoadingPortfolios(true);
      setPortfolioStatus(null);
      try {
        const user = await AccountAuth.zeosUserAsync();
        setCurrentZeosUserId(user?.zeosUserId ?? null);
        setCanBrowseAllPortfolios(Boolean(user?.isSuperAdmin));

        let portfolios: PortfolioOption[] = (user?.customerClaims || [])
          .flatMap((customer) =>
            (customer.companies || []).map((company) => ({
              company,
              customer,
            }))
          )
          .map(({ company, customer }) => ({
            companyId: company.companyId,
            companyName: company.companyName,
            customerId: customer.customerId,
            customerName: customer.customerName,
          }));

        if (user?.isSuperAdmin) {
          const adminResponse = await api.Admin.getCustomersAsync();
          portfolios = (adminResponse?.data || []).flatMap((customer) =>
            (customer.companies || []).map((company) => ({
              companyId: company.companyId,
              companyName: company.name,
              customerId: customer.customerId,
              customerName: customer.name,
            }))
          );
        }

        portfolios = portfolios
          .map((company) => ({
            companyId: company.companyId,
            companyName: company.companyName,
            customerId: company.customerId,
            customerName: company.customerName,
          }))
          .filter((company) => company.companyId && company.companyName);
        const unique = mergePortfolioOptions(portfolios);
        if (activeCompany && !unique.some((company) => company.companyId === activeCompany.companyId)) {
          unique.unshift({
            companyId: activeCompany.companyId,
            companyName: activeCompany.companyName,
          });
        }
        if (!isMounted) return;
        setAvailablePortfolios(unique);
        const defaultCompanyId = urlCompanyId ?? activeCompany?.companyId ?? unique[0]?.companyId ?? null;
        setSelectedExistingCompanyId((current) => current ?? defaultCompanyId);
      } catch (error: any) {
        if (!isMounted) return;
        const fallback = activeCompany
          ? [{ companyId: activeCompany.companyId, companyName: activeCompany.companyName }]
          : [];
        setAvailablePortfolios(fallback);
        setPortfolioStatus(error?.message || "Unable to load the full company list. Showing available account companies.");
        setSelectedExistingCompanyId((current) => current ?? urlCompanyId ?? activeCompany?.companyId ?? null);
      } finally {
        if (isMounted) {
          setIsLoadingPortfolios(false);
        }
      }
    };

    loadPortfoliosAsync();
    return () => {
      isMounted = false;
    };
  }, [activeCompany, api.Admin, urlCompanyId]);

  useEffect(() => {
    if (canBrowseAllPortfolios || !currentZeosUserId || portfolioQuery.trim().length < 2) {
      return undefined;
    }

    let isMounted = true;
    const searchCompaniesAsync = async () => {
      try {
        const response = await api.Company.searchCompaniesAsync({
          name: portfolioQuery.trim(),
          zeosUserId: currentZeosUserId,
        });
        if (!isMounted || !response?.data?.length) return;
        const matches = response.data.map((company) => ({
          companyId: company.companyId ?? 0,
          companyName: company.companyName,
          customerId: company.customerId ?? null,
        }));
        setAvailablePortfolios((current) => mergePortfolioOptions([...current, ...matches]));
      } catch {
        if (isMounted) {
          setPortfolioStatus("Company search is unavailable. Showing loaded companies.");
        }
      }
    };

    const timeoutId = window.setTimeout(searchCompaniesAsync, 250);
    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [api.Company, canBrowseAllPortfolios, currentZeosUserId, portfolioQuery]);

  useEffect(() => {
    if (!activeCompany || initializedExistingCompanyId === activeCompany.companyId) {
      return;
    }

    if (!selectedExistingCompanyId) {
      setSelectedExistingCompanyId(urlCompanyId ?? activeCompany.companyId);
    }

    setInitializedExistingCompanyId(activeCompany.companyId);
  }, [activeCompany, initializedExistingCompanyId, selectedExistingCompanyId, urlCompanyId]);

  useEffect(() => {
    if (!selectedExistingCompanyId) {
      setExistingProperties([]);
      setSelectedExistingPropertyDetails(null);
      return;
    }

    let isMounted = true;
    const loadExistingPropertiesAsync = async () => {
      setIsLoadingExistingProperties(true);
      setExistingPropertyStatus(null);
      try {
        const response = await api.Property.propertiesByCompanyId(selectedExistingCompanyId);
        if (!isMounted) return;
        // Sandbox clones arrive only for super admins; the API withholds them from everyone else,
        // so this picker shows whatever the caller is entitled to see without filtering here.
        const properties = response?.data || [];
        setExistingProperties(properties);
        if (urlPropertyId && properties.some((property) => property.propertyId === urlPropertyId)) {
          setSelectedExistingPropertyId(urlPropertyId);
          const selected = properties.find((property) => property.propertyId === urlPropertyId);
          setPropertyQuery(selected?.propertyName ?? "");
        } else if (selectedExistingPropertyId && properties.some((property) => property.propertyId === selectedExistingPropertyId)) {
          const selected = properties.find((property) => property.propertyId === selectedExistingPropertyId);
          setPropertyQuery(selected?.propertyName ?? "");
        } else {
          setSelectedExistingPropertyId(null);
          setPropertyQuery("");
        }
      } catch (error: any) {
        if (!isMounted) return;
        setExistingProperties([]);
        setSelectedExistingPropertyId(null);
        setSelectedExistingPropertyDetails(null);
        setExistingPropertyStatus(error?.message || "Unable to load properties for this portfolio.");
      } finally {
        if (isMounted) {
          setIsLoadingExistingProperties(false);
        }
      }
    };

    loadExistingPropertiesAsync();
    return () => {
      isMounted = false;
    };
  }, [api.Property, selectedExistingCompanyId, urlPropertyId]);

  useEffect(() => {
    if (!selectedExistingPropertyId) {
      setSelectedExistingPropertyDetails(null);
      return;
    }

    let isMounted = true;
    const loadPropertyDetailsAsync = async () => {
      setIsLoadingExistingPropertyDetails(true);
      setExistingPropertyStatus(null);
      try {
        const response = await api.Property.getProperty(selectedExistingPropertyId);
        const property = response?.data || selectedExistingProperty;
        if (!isMounted || !property) return;
        setSelectedExistingPropertyDetails(property);
        const espmSelection = resolveEspmSelectionFromIds(
          espmCategories,
          property.espmPropertyTypeCategoryId,
          property.espmPropertyTypeDetailId
        );
        const matchingFacts = property as IPropertyContract & {
          buildingUses?: unknown;
          ownerClass?: string | null;
          isAffordableHousing?: boolean | null;
          isHistoric?: boolean | null;
          isPrimaryResidence?: boolean | null;
          isBarn?: boolean | null;
          isIncomeQualified?: boolean | null;
        };
        setForm((current) => ({
          ...current,
          address: formatPropertyAddress(property) || current.address || property.propertyName,
          companyName: selectedExistingCompany?.companyName || current.companyName,
          utility: [property.electricUtilityProvider, property.gasUtilityProvider]
            .filter((name) => {
              const v = (name ?? "").trim().toLowerCase();
              return v && v !== "none" && v !== "n/a" && v !== "na" && v !== "no piped gas";
            })
            .join(" / ") || current.utility,
          propertyType: espmSelection.propertyType,
          espmPropertyTypeCategoryId: espmSelection.categoryId,
          espmPropertyTypeDetailId: espmSelection.detailId,
          buildingUses:
            Array.isArray(matchingFacts.buildingUses)
              ? normalizeBuildingUses(matchingFacts.buildingUses)
              : current.buildingUses,
          ownerClass: matchingFacts.ownerClass ?? current.ownerClass,
          isAffordableHousing:
            matchingFacts.isAffordableHousing ?? current.isAffordableHousing,
          isHistoric: matchingFacts.isHistoric ?? current.isHistoric,
          isPrimaryResidence:
            matchingFacts.isPrimaryResidence ?? current.isPrimaryResidence,
          isBarn: matchingFacts.isBarn ?? current.isBarn,
          isIncomeQualified:
            matchingFacts.isIncomeQualified ?? current.isIncomeQualified,
          squareFeet: property.sqFt ? String(property.sqFt) : current.squareFeet,
          optionalCharacteristics:
            buildHydratedPropertyCharacteristics(property, hvacOptions) || current.optionalCharacteristics,
        }));
      } catch (error: any) {
        if (!isMounted) return;
        setSelectedExistingPropertyDetails(selectedExistingProperty);
        setExistingPropertyStatus(error?.message || "Unable to load full property details. Basic property data is still selected.");
      } finally {
        if (isMounted) {
          setIsLoadingExistingPropertyDetails(false);
        }
      }
    };

    loadPropertyDetailsAsync();
    return () => {
      isMounted = false;
    };
  }, [api.Property, espmCategories, hvacOptions, selectedExistingCompany?.companyName, selectedExistingProperty, selectedExistingPropertyId]);

  const updateForm = <K extends keyof IntakeFormState>(
    field: K,
    value: IntakeFormState[K],
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const toggleUtility = (utility: string) => {
    const nextUtilities = selectedUtilities.includes(utility)
      ? selectedUtilities.filter((selected) => selected !== utility)
      : [...selectedUtilities, utility];
    updateForm("utility", nextUtilities.join(", "));
  };

  const selectPortfolio = (portfolio: PortfolioOption) => {
    setSelectedExistingCompanyId(portfolio.companyId);
    setPortfolioQuery(portfolio.companyName);
    setSelectedExistingPropertyId(null);
    setSelectedExistingPropertyDetails(null);
    setPropertyQuery("");
    setIsPortfolioDropdownOpen(false);
    setIsPropertyDropdownOpen(false);
    setForm((current) => ({
      ...current,
      companyName: portfolio.companyName,
    }));
  };

  const selectExistingProperty = (property: IPropertyContract) => {
    setSelectedExistingPropertyId(property.propertyId ?? null);
    setPropertyQuery(property.propertyName);
    setSelectedExistingPropertyDetails(property);
    setIsPropertyDropdownOpen(false);
  };

  useEffect(() => {
    if (!realRunId || !liveRun || isTerminalRunStatus(liveRun.status)) {
      if (liveRun && isTerminalRunStatus(liveRun.status)) {
        setIsRunning(false);
        setPollCountdown(0);
      }
      return undefined;
    }

    setPollCountdown(RUN_POLL_INTERVAL_SECONDS);

    const pollRun = async () => {
      try {
        const refreshed = await api.Screening.getRunAsync(realRunId);
        if (refreshed?.data) {
          setLiveRun(refreshed.data);
          setRunStatus(buildRunStatusMessage(refreshed.data));
          if (isTerminalRunStatus(refreshed.data.status)) {
            setIsRunning(false);
            setPollCountdown(0);
          } else {
            setPollCountdown(RUN_POLL_INTERVAL_SECONDS);
          }
        }
      } catch (error: any) {
        setRunStatus(error?.message || "Unable to refresh screening status.");
        setPollCountdown(RUN_POLL_INTERVAL_SECONDS);
      }
    };

    const countdownIntervalId = window.setInterval(() => {
      setPollCountdown((current) =>
        current <= 1 ? RUN_POLL_INTERVAL_SECONDS : current - 1
      );
    }, 1000);
    const intervalId = window.setInterval(pollRun, 15000);
    return () => {
      window.clearInterval(countdownIntervalId);
      window.clearInterval(intervalId);
    };
  }, [api.Screening, liveRun, realRunId]);

  const validateForm = () => {
    if (mode === "existing" && !selectedExistingCompanyId) {
      return "Select a portfolio before running screening.";
    }

    if (mode === "existing" && !selectedExistingPropertyId) {
      return "Select a property before running screening.";
    }

    if (mode === "existing" && isLoadingExistingPropertyDetails) {
      return "Wait for property details to finish loading before running screening.";
    }

    if (mode !== "existing" && !form.address.trim()) {
      return "Enter a location or address before running screening.";
    }

    if (mode === "new") {
      const missingFields = [
        !form.companyName.trim() && "Company",
        !form.utility.trim() && "Utility",
        !form.squareFeet.trim() && "Square feet",
        !form.economicObjectives.trim() && "Economic objectives",
      ].filter(Boolean);

      if (missingFields.length > 0) {
        return `Add required fields for a new customer: ${missingFields.join(", ")}.`;
      }
    }

    return null;
  };

  const buildIntakePayload = () => {
    const property = mode === "existing"
      ? selectedExistingPropertyDetails ?? selectedExistingProperty
      : null;
    const propertyContext = property
      ? `${property.propertyName} (property ${property.propertyId})`
      : null;
    const address = form.address.trim() || propertyContext || "";
    // Prefer the property's actual yearBuilt column -- property.age is a separate, legacy column
    // that isn't always kept in sync (e.g. yearBuilt set, age left empty), so deriving yearBuilt
    // from age alone can silently drop real data. Only fall back to age-derived values when the
    // more direct source is missing.
    const yearBuilt =
      form.yearBuilt.trim() ||
      (property?.yearBuilt
        ? property.yearBuilt.toString()
        : property?.age
        ? `${new Date().getFullYear() - property.age}`
        : null);
    const buildingAge =
      form.buildingAge.trim() ||
      (property?.age
        ? property.age.toString()
        : yearBuilt
        ? `${new Date().getFullYear() - Number(yearBuilt)}`
        : null);
    const lastRetrofitYear = form.lastRetrofitYear.trim() || null;

    return buildScreeningIntake({
      source: "screening-dashboard",
      mode,
      address,
      companyId: mode === "existing" ? selectedExistingCompanyId : activeCompany?.companyId ?? urlCompanyId ?? null,
      companyName: form.companyName.trim() || selectedExistingCompany?.companyName || activeCompany?.companyName || null,
      utility: form.utility.trim() || null,
      electricProvider: property?.electricUtilityProvider || null,
      gasProvider: property?.gasUtilityProvider || null,
      propertyType: form.propertyType.trim() || null,
      espmPropertyTypeCategoryId: form.espmPropertyTypeCategoryId ?? null,
      espmPropertyTypeDetailId: form.espmPropertyTypeDetailId ?? null,
      buildingUses: normalizeBuildingUses(form.buildingUses),
      ownerClass: form.ownerClass,
      isAffordableHousing: form.isAffordableHousing,
      isHistoric: form.isHistoric,
      isPrimaryResidence: form.isPrimaryResidence,
      isBarn: form.isBarn,
      isIncomeQualified: form.isIncomeQualified,
      squareFeet: form.squareFeet.trim() || null,
      buildingAge,
      yearBuilt,
      lastRetrofitYear,
      economicObjectives: form.economicObjectives.trim() || null,
      optionalCharacteristics: form.optionalCharacteristics.trim() || null,
      propertyId: mode === "existing" ? selectedExistingPropertyId : null,
      propertySnapshot: property
        ? {
            propertyId: property.propertyId,
            propertyName: property.propertyName,
            address: formatPropertyAddress(property),
            city: property.city,
            state: property.state,
            zip: property.zip,
            squareFeet: property.sqFt,
            units: property.units,
            floors: property.floors,
            age: property.age,
            buildingAge,
            yearBuilt,
            lastRetrofitYear,
            hvac: property.hvac,
            roofSize: property.roofSize,
            bbl: property.bbl,
            bin: property.bin,
            utilityIds: property.utilityIds,
            electricUtilityProvider: property.electricUtilityProvider,
            gasUtilityProvider: property.gasUtilityProvider,
          }
        : null,
    });
  };

  const startScreening = async () => {
    const validationMessage = validateForm();
    if (validationMessage) {
      setFormError(validationMessage);
      return;
    }

    setFormError(null);
    setIsRunning(true);
    setLiveRun(null);
    setPollCountdown(RUN_POLL_INTERVAL_SECONDS);
    setRunStatus("Creating screening run...");

    try {
      const { intakeText, intakeJson } = buildIntakePayload();
      const created = await api.Screening.createRunAsync({
        serviceProviderId: 1,
        propertyId: mode === "existing" ? selectedExistingPropertyId : null,
        intakeText,
        intakeJson: JSON.stringify(intakeJson),
        documentReferences: [],
      });

      if (created?.zeosStatus && created.zeosStatus.toLowerCase() !== "success") {
        throw new Error(created.message || "Unable to create screening run.");
      }

      const createdRunId = created?.data?.runId ?? created?.data?.screeningRunId;
      if (!createdRunId) {
        throw new Error(created?.message || "Run creation did not return an id.");
      }

      setLiveRun(created.data);
      if (created.data?.runGuid) {
        setInitializedRunGuid(created.data.runGuid);
        setRunGuidInUrl(created.data.runGuid);
      }
      setRunStatus("Queued screening. Starting agentic screening...");
      loadRunHistory();

      const started = await api.Screening.startRunAsync(createdRunId);
      if (started?.data) {
        setLiveRun(started.data);
        if (started.data.runGuid) {
          setInitializedRunGuid(started.data.runGuid);
          setRunGuidInUrl(started.data.runGuid);
        }
        setRunStatus(`${buildRunStatusMessage(started.data)} Checking every ${RUN_POLL_INTERVAL_SECONDS} seconds.`);
        if (isTerminalRunStatus(started.data.status)) {
          setIsRunning(false);
          setPollCountdown(0);
          loadRunHistory();
        } else {
          setPollCountdown(RUN_POLL_INTERVAL_SECONDS);
        }
      }
    } catch (error: any) {
      setRunStatus(error?.message || "Unable to run screening.");
      setIsRunning(false);
      setPollCountdown(0);
    }
  };

  const resolveReportFileName = (
    contentDisposition: string | undefined,
    format: ReportFormat
  ) => {
    const fallback = `${realRunId}-screening-report.${format}`;
    if (!contentDisposition) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    return match?.[1] ? decodeURIComponent(match[1]) : fallback;
  };

  const downloadReportBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportReport = async (format: ReportFormat) => {
    if (!realRunId) {
      setExportError("Run a screening before exporting a report.");
      return;
    }

    if (isPreliminaryRun && !acknowledgedPreliminary) {
      setExportError(
        "This run is preliminary. Acknowledge the notice above, or re-run in Existing Portfolio Property mode, before exporting a report."
      );
      return;
    }

    try {
      setExportError(null);
      const apiFormat = toApiReportFormat(format);
      const generated = await api.Screening.generateReportAsync(realRunId, {
        format: apiFormat,
        reportType: "screening-summary",
        notifyWhenReady: true,
      });
      if (generated?.zeosStatus && generated.zeosStatus.toLowerCase() !== "success") {
        throw new Error(generated.message || "The report event could not be recorded.");
      }

      const refreshed = await api.Screening.getRunAsync(realRunId);
      if (refreshed?.data) {
        setLiveRun(refreshed.data);
      }

      const download = await api.Screening.downloadReportAsync(
        realRunId,
        "screening-summary",
        apiFormat
      );
      if (download?.zeosStatus && download.zeosStatus.toLowerCase() !== "success") {
        throw new Error("Report download failed.");
      }
      if (!download.rawResponse) {
        throw new Error("Report download returned no file.");
      }

      const contentType =
        (download.rawResponse.headers?.["content-type"] as string) ||
        (format === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
      const blob = new Blob([download.rawResponse.data], { type: contentType });
      const fileName = resolveReportFileName(
        download.rawResponse.headers?.["content-disposition"],
        format
      );
      downloadReportBlob(blob, fileName);
      Analytics.capture("report_exported", {
        format,
        report_type: "screening-summary",
      });
    } catch (error: any) {
      setExportError(error?.message || "Report download failed.");
    }
  };

  const openIncentiveMatching = () => {
    if (!realRunGuid && !activePropertyId) {
      setRunStatus("Run a screening before opening Incentive Matching.");
      return;
    }

    const params = new URLSearchParams();
    const companyId = mode === "existing"
      ? selectedExistingCompanyId ?? activeCompany?.companyId ?? urlCompanyId
      : liveRun?.incentiveMatchRequest?.companyId ?? activeCompany?.companyId ?? urlCompanyId;
    if (companyId) {
      params.set("company", companyId.toString());
    }
    // A run held on utilities has no screening result; open the property's matches instead of
    // an empty screening-derived list (UTL P07: held is never "no programs found").
    if (realRunGuid && !utilityHold) {
      params.set("screeningRun", realRunGuid);
    } else if (activePropertyId) {
      params.set("property", activePropertyId.toString());
      params.set("updateSummary", "1");
    }
    navigate(`/incentives?${params.toString()}`);
  };

  const renderField = (
    field: TextIntakeField,
    label: string,
    placeholder: string,
    required = false,
    options?: {
      disableBrowserSuggestions?: boolean;
      inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
    }
  ) => (
    <label className="block">
      <span className="text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-zeos-orange-dk"> *</span>}
      </span>
      <input
        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
        value={form[field]}
        onChange={(event) => updateForm(field, event.target.value)}
        placeholder={placeholder}
        name={options?.disableBrowserSuggestions ? `zeos-screening-${field}` : field}
        autoComplete={options?.disableBrowserSuggestions ? "new-password" : undefined}
        autoCorrect={options?.disableBrowserSuggestions ? "off" : undefined}
        spellCheck={options?.disableBrowserSuggestions ? false : undefined}
        inputMode={options?.inputMode}
      />
    </label>
  );

  const renderUtilityMultiSelect = () => (
    <div className="block">
      <span className="text-xs font-semibold text-slate-700">
        Utility
        {mode === "new" && <span className="text-zeos-orange-dk"> *</span>}
      </span>
      <div className="relative mt-1">
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-1.5 text-left text-sm text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
          onClick={() => setIsUtilityDropdownOpen((current) => !current)}
          onBlur={() => window.setTimeout(() => setIsUtilityDropdownOpen(false), 120)}
          aria-haspopup="listbox"
          aria-expanded={isUtilityDropdownOpen}
        >
          <span className={selectedUtilities.length ? "truncate" : "text-slate-500"}>
            {selectedUtilities.length
              ? selectedUtilities.join(", ")
              : "Select one or more utilities"}
          </span>
          <span className="ml-2 text-xs font-semibold text-slate-400">v</span>
        </button>
        {isUtilityDropdownOpen && (
          <div
            className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-lg"
            role="listbox"
            aria-multiselectable="true"
          >
            {utilityOptionsByRegion.map((group) => (
              <div key={group.region} className="py-1">
                <div className="px-2 py-1 text-xs font-semibold uppercase text-slate-500">
                  {group.region}
                </div>
                {group.options.map((utility) => (
                  <label
                    key={`${group.region}-${utility}`}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-zeos-orange-dk focus:ring-orange-100"
                      checked={selectedUtilities.includes(utility)}
                      onChange={() => toggleUtility(utility)}
                      name={`zeos-screening-utility-${group.region}-${utility}`}
                      autoComplete="new-password"
                    />
                    <span>{utility}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderBuildingUses = () => (
    <fieldset className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <legend className="px-1 text-xs font-semibold text-slate-700">Building use</legend>
      <p className="mb-2 text-xs text-slate-500">
        Choose every use present. Mixed use is a separate choice and does not add another use.
      </p>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {BUILDING_USE_OPTIONS.map((option) => (
          <label key={option.id} className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={form.buildingUses.includes(option.id)}
              onChange={(event) => {
                const next = event.target.checked
                  ? [...form.buildingUses, option.id]
                  : form.buildingUses.filter((use) => use !== option.id);
                updateForm("buildingUses", next);
              }}
              className="h-4 w-4 rounded border-slate-300 text-zeos-orange-dk focus:ring-orange-100"
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );

  const renderIntakeFact = (
    field:
      | "isAffordableHousing"
      | "isHistoric"
      | "isIncomeQualified",
    label: string,
  ) => {
    const value = form[field];
    return (
      <label className="block text-xs font-semibold text-slate-700">
        <span className="mb-1 block">{label}</span>
        <select
          value={value === true ? "true" : value === false ? "false" : "unknown"}
          onChange={(event) => {
            const next = event.target.value;
            updateForm(field, next === "true" ? true : next === "false" ? false : null);
          }}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-normal text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
        >
          <option value="unknown">Not known</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </label>
    );
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DashboardHeader companyCallback={setActiveCompany} />
      <main className="flex-grow px-4 py-4">
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase text-zeos-orange-dk">
                Service Provider Screening
              </p>
              <h1 className="mt-1 text-xl font-semibold text-slate-950">
                Start a screening from an address or customer property
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                Enter a location, add the known property characteristics, then run screening to
                produce results, report exports, and an Incentive Matching handoff when a property is attached.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { value: "quick", label: "Quick Screening" },
              { value: "new", label: "Add New Customer" },
              { value: "existing", label: "Existing Portfolio Property" },
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                className={`rounded-md border px-3 py-1.5 text-sm font-semibold ${
                  mode === item.value
                    ? "border-zeos-orange-dk bg-orange-50 text-zeos-orange-dk"
                    : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                }`}
                onClick={() => setMode(item.value as ScreeningMode)}
              >
                {item.label}
              </button>
            ))}
            {showDemoTools && (
              <button
                type="button"
                className="rounded-md border border-sky-300 bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-800 hover:border-sky-400"
                onClick={() => setIsDemoAddressPickerOpen((current) => !current)}
                aria-expanded={isDemoAddressPickerOpen}
              >
                Demo addresses
              </button>
            )}
          </div>

          {showDemoTools && isDemoAddressPickerOpen && (
            <div className="mt-3 rounded-md border border-sky-200 bg-sky-50 p-2">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                {demoScreeningProfiles.map((profile) => (
                  <button
                    key={profile.address}
                    type="button"
                    className="rounded border border-sky-200 bg-white px-2 py-1.5 text-left text-xs font-semibold text-slate-800 hover:border-sky-400 hover:bg-sky-100"
                    onClick={() => {
                      setForm(profile);
                      setMode("quick");
                      setIsDemoAddressPickerOpen(false);
                      setFormError(null);
                    }}
                  >
                    {profile.address}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-3 md:grid-cols-2">
                {mode === "existing" && (
                  <>
                    <div className="relative block">
                      <span className="text-xs font-semibold text-slate-700">
                        Portfolio <span className="text-zeos-orange-dk">*</span>
                      </span>
                      <div className="relative mt-1">
                        <input
                          className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 pr-8 text-sm text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
                          value={portfolioQuery}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setPortfolioQuery(nextValue);
                            setIsPortfolioDropdownOpen(true);
                            if (selectedExistingCompany && nextValue !== selectedExistingCompany.companyName) {
                              setSelectedExistingCompanyId(null);
                              setSelectedExistingPropertyId(null);
                              setSelectedExistingPropertyDetails(null);
                              setPropertyQuery("");
                            }
                          }}
                          onFocus={() => setIsPortfolioDropdownOpen(true)}
                          onBlur={() => window.setTimeout(() => setIsPortfolioDropdownOpen(false), 120)}
                          placeholder={selectedExistingCompany?.companyName || "Type to search portfolios"}
                          role="combobox"
                          aria-expanded={isPortfolioDropdownOpen}
                          aria-controls="screening-portfolio-options"
                          autoComplete="off"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">
                          v
                        </span>
                      </div>
                      {isPortfolioDropdownOpen && (
                        <div
                          id="screening-portfolio-options"
                          className="absolute z-30 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
                          role="listbox"
                        >
                          {isLoadingPortfolios && (
                            <div className="px-3 py-2 text-sm text-slate-500">
                              Loading portfolios...
                            </div>
                          )}
                          {filteredPortfolios.map((portfolio) => (
                            <button
                              key={portfolio.companyId}
                              type="button"
                              role="option"
                              aria-selected={portfolio.companyId === selectedExistingCompanyId}
                              className={`block w-full px-3 py-2 text-left text-sm ${
                                portfolio.companyId === selectedExistingCompanyId
                                  ? "bg-orange-50 font-semibold text-slate-950"
                                  : "text-slate-700 hover:bg-slate-50"
                              }`}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                selectPortfolio(portfolio);
                              }}
                            >
                              <span className="block truncate">{portfolio.companyName}</span>
                              {portfolio.customerName && (
                                <span className="block truncate text-xs font-normal text-slate-500">
                                  {portfolio.customerName}
                                </span>
                              )}
                            </button>
                          ))}
                          {!isLoadingPortfolios && filteredPortfolios.length === 0 && (
                            <div className="px-3 py-2 text-sm text-slate-500">
                              No matching portfolios.
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="relative block">
                      <span className="text-xs font-semibold text-slate-700">
                        Property <span className="text-zeos-orange-dk">*</span>
                      </span>
                      <div className="relative mt-1">
                        <input
                          className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 pr-8 text-sm text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100 disabled:bg-slate-100 disabled:text-slate-500"
                          value={propertyQuery}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setPropertyQuery(nextValue);
                            setIsPropertyDropdownOpen(true);
                            if (selectedExistingProperty && nextValue !== selectedExistingProperty.propertyName) {
                              setSelectedExistingPropertyId(null);
                              setSelectedExistingPropertyDetails(null);
                            }
                          }}
                          onFocus={() => selectedExistingCompanyId && setIsPropertyDropdownOpen(true)}
                          onBlur={() => window.setTimeout(() => setIsPropertyDropdownOpen(false), 120)}
                          placeholder={
                            selectedExistingCompanyId
                              ? "Type property name, address, ZIP, BBL, or BIN"
                              : "Select a portfolio first"
                          }
                          disabled={!selectedExistingCompanyId}
                          name={`zeos-screening-property-lookup-${selectedExistingCompanyId ?? "none"}`}
                          role="combobox"
                          aria-expanded={isPropertyDropdownOpen}
                          aria-controls="screening-property-options"
                          aria-autocomplete="list"
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">
                          v
                        </span>
                      </div>
                      {isPropertyDropdownOpen && (
                        <div
                          id="screening-property-options"
                          className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
                          role="listbox"
                        >
                          {isLoadingExistingProperties && (
                            <div className="px-3 py-2 text-sm text-slate-500">
                              Loading properties...
                            </div>
                          )}
                          {!isLoadingExistingProperties &&
                            filteredExistingProperties.map((property) => (
                              <button
                                key={property.propertyId}
                                type="button"
                                role="option"
                                aria-selected={property.propertyId === selectedExistingPropertyId}
                                className={`block w-full px-3 py-2 text-left text-sm ${
                                  property.propertyId === selectedExistingPropertyId
                                    ? "bg-orange-50 font-semibold text-slate-950"
                                    : "text-slate-700 hover:bg-slate-50"
                                }`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  selectExistingProperty(property);
                                }}
                              >
                                <span className="block truncate">{property.propertyName}</span>
                                <span className="block truncate text-xs font-normal text-slate-500">
                                  {[formatPropertyAddress(property), property.bbl && `BBL ${property.bbl}`, property.bin && `BIN ${property.bin}`]
                                    .filter(Boolean)
                                    .join(" - ") || "No address metadata"}
                                </span>
                              </button>
                            ))}
                          {!isLoadingExistingProperties &&
                            selectedExistingCompanyId &&
                            filteredExistingProperties.length === 0 && (
                              <div className="px-3 py-2 text-sm text-slate-500">
                                No properties in this portfolio match the search.
                              </div>
                            )}
                          {!selectedExistingCompanyId && (
                            <div className="px-3 py-2 text-sm text-slate-500">
                              Select a portfolio first.
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {existingPropertyStatus && (
                      <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                        {existingPropertyStatus}
                      </div>
                    )}
                    {portfolioStatus && (
                      <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                        {portfolioStatus}
                      </div>
                    )}
                    {isLoadingExistingPropertyDetails && (
                      <div className="md:col-span-2 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-600">
                        Loading full property details...
                      </div>
                    )}
                  </>
                )}

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold text-slate-700">
                    Location/address
                    {mode !== "existing" && <span className="text-zeos-orange-dk"> *</span>}
                  </span>
                  <input
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
                    value={form.address}
                    onChange={(event) => updateForm("address", event.target.value)}
                    placeholder="315 Gates Avenue, Brooklyn, NY"
                  />
                </label>

                {renderField("companyName", "Company", "Customer or company name", mode === "new")}
                {renderUtilityMultiSelect()}
                <div className="block">
                  <EspmPropertyTypeTypeahead
                    categories={espmCategories}
                    loading={espmLoading}
                    value={{
                      categoryId: form.espmPropertyTypeCategoryId,
                      detailId: form.espmPropertyTypeDetailId,
                    }}
                    onChange={(next, label) => {
                      setForm((current) => ({
                        ...current,
                        espmPropertyTypeCategoryId: next?.categoryId,
                        espmPropertyTypeDetailId: next?.detailId,
                        propertyType: label,
                      }));
                    }}
                  />
                </div>
                <div className="grid gap-3 md:col-span-2 md:grid-cols-2">
                  {renderBuildingUses()}
                  <fieldset className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <legend className="px-1 text-xs font-semibold text-slate-700">
                      Owner or applicant
                    </legend>
                    <p className="mb-2 text-xs text-slate-500">
                      This is the applicant class for matching. It is separate from building use.
                    </p>
                    <label className="block text-xs font-semibold text-slate-700">
                      <span className="mb-1 block">Owner class</span>
                      <select
                        value={form.ownerClass ?? ""}
                        onChange={(event) =>
                          updateForm("ownerClass", event.target.value || null)
                        }
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-normal text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
                      >
                        <option value="">Not known</option>
                        {OWNER_CLASS_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {OWNER_CLASS_LABELS[option]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </fieldset>
                  <fieldset className="rounded-md border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                    <legend className="px-1 text-xs font-semibold text-slate-700">
                      Additional facts
                    </legend>
                    <p className="mb-2 text-xs text-slate-500">
                      Leave a fact as Not known when intake has not established it. Unknown facts are kept separate from No.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {renderIntakeFact("isAffordableHousing", "Affordable housing")}
                      {renderIntakeFact("isHistoric", "Historic")}
                      {renderIntakeFact("isIncomeQualified", "Income qualified")}
                    </div>
                  </fieldset>
                </div>
                {renderField("squareFeet", "Square feet", "30000", mode === "new")}
                {renderField("buildingAge", "Building age", "", false, {
                  disableBrowserSuggestions: true,
                  inputMode: "numeric",
                })}
                {renderField("yearBuilt", "Year built", "", false, {
                  disableBrowserSuggestions: true,
                  inputMode: "numeric",
                })}
                {renderField("lastRetrofitYear", "Last retrofit year", "", false, {
                  disableBrowserSuggestions: true,
                  inputMode: "numeric",
                })}

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold text-slate-700">
                    Economic objectives
                    {mode === "new" && <span className="text-zeos-orange-dk"> *</span>}
                  </span>
                  <textarea
                    className="mt-1 min-h-16 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
                    value={form.economicObjectives}
                    onChange={(event) => updateForm("economicObjectives", event.target.value)}
                    placeholder="Lower energy costs, reduce capex, maximize rebates, prioritize fast payback."
                  />
                </label>

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold text-slate-700">
                    Optional characteristics
                  </span>
                  <textarea
                    className="mt-1 min-h-16 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-950 outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100"
                    value={form.optionalCharacteristics}
                    onChange={(event) => updateForm("optionalCharacteristics", event.target.value)}
                    placeholder="Steam heat, rent-regulated units, planned electrification, age, units, roof area, equipment notes."
                  />
                </label>
              </div>

              {formError && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
                  {formError}
                </div>
              )}

              {runStatus && (
                <div
                  className={`mt-3 rounded-md border p-2 text-sm ${
                    /failed|unable|error|did not/i.test(runStatus)
                      ? "border-red-200 bg-red-50 text-red-800"
                      : "border-slate-200 bg-slate-50 text-slate-700"
                  }`}
                >
                  {runStatus}
                </div>
              )}

              {utilityHold && (
                <div
                  className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                  data-testid="screening-utility-hold"
                  role="status"
                >
                  <p className="font-semibold">Screening is on hold</p>
                  {utilityHold.services.length > 0 ? (
                    <ul className="mt-1 list-disc pl-5">
                      {utilityHold.services.map((service) => (
                        <li key={service.serviceCode}>{service.message}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1">{utilityHold.reason}</p>
                  )}
                  <p className="mt-1">
                    Screening runs once the serving utilities are confirmed. This is not a
                    &quot;no programs&quot; result.
                  </p>
                  {utilityHold.propertyId > 0 && (
                    <button
                      type="button"
                      className="mt-2 text-sm font-semibold text-zeos-green underline"
                      onClick={() => {
                        // The property may belong to a company other than the active one; Portfolio
                        // switches to it before looking the property up.
                        const params = new URLSearchParams({ editProperty: String(utilityHold.propertyId) });
                        const holdCompanyId = mode === "existing"
                          ? selectedExistingCompanyId ?? liveRun?.companyId ?? activeCompany?.companyId ?? urlCompanyId
                          : liveRun?.companyId ?? liveRun?.incentiveMatchRequest?.companyId ?? activeCompany?.companyId ?? urlCompanyId;
                        if (holdCompanyId) {
                          params.set("company", holdCompanyId.toString());
                        }
                        navigate(`/portfolio?${params.toString()}`);
                      }}
                    >
                      Confirm utilities on the property
                    </button>
                  )}
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md bg-zeos-orange-dk px-4 py-1.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-70"
                  onClick={startScreening}
                  disabled={isRunning}
                >
                  {isRunning && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {isRunning ? "Running Screening..." : "Run Screening"}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400"
                  onClick={() => {
                    setForm(emptyIntakeForm);
                    setLiveRun(null);
                    setRunStatus(null);
                    setFormError(null);
                    setIsRunning(false);
                    setPollCountdown(0);
                    setInitializedRunGuid(null);
                    setRunGuidInUrl(null);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            <aside className="rounded-lg border border-slate-200 bg-white p-4">
              <h2 className="text-base font-semibold text-slate-950">Screening path</h2>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <div className="rounded-md bg-slate-50 p-2">
                  <p className="font-semibold text-slate-900">1. Location</p>
                  <p>{form.address || selectedExistingProperty?.propertyName || "No address entered yet."}</p>
                </div>
                <div className="rounded-md bg-slate-50 p-2">
                  <p className="font-semibold text-slate-900">2. Characteristics</p>
                  <p>
                    {[
                      form.utility,
                      form.propertyType,
                      form.squareFeet && `${form.squareFeet} sq ft`,
                      form.buildingAge && `${form.buildingAge} years old`,
                      form.yearBuilt && `built ${form.yearBuilt}`,
                      form.lastRetrofitYear && `retrofit ${form.lastRetrofitYear}`,
                    ]
                      .filter(Boolean)
                      .join(" - ") || "Optional for quick screenings."}
                  </p>
                </div>
                <div className="rounded-md bg-slate-50 p-2">
                  <p className="font-semibold text-slate-900">3. Output</p>
                  <p>{liveRun ? `Screening is ${liveRun.status}.` : "Run screening to generate results."}</p>
                </div>
              </div>
              <div className="mt-4 border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-base font-semibold text-slate-950">Run History</h2>
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-slate-400"
                    onClick={loadRunHistory}
                    disabled={isHistoryLoading}
                  >
                    {isHistoryLoading ? "Loading..." : "Refresh"}
                  </button>
                </div>
                {historyStatus && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                    {historyStatus}
                  </div>
                )}
                <div className="mt-2 max-h-80 space-y-2 overflow-y-auto pr-1">
                  {runHistory.map((item) => {
                    const isSelected = item.runGuid === realRunGuid || item.runGuid === urlRunGuid;
                    return (
                      <button
                        key={item.runGuid}
                        type="button"
                        className={`w-full rounded-md border p-2 text-left text-xs ${
                          isSelected
                            ? "border-zeos-orange-dk bg-orange-50 text-slate-950"
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white"
                        }`}
                        onClick={() => loadRunByGuid(item.runGuid)}
                        disabled={isLoadingSavedRun}
                      >
                        <span className="block truncate font-semibold">{item.title}</span>
                        <span className="mt-1 block text-slate-500">
                          {item.status} - {item.completedStageCount}/{item.totalStageCount || 8} stages
                        </span>
                        <span className="mt-1 block text-slate-500">
                          {formatHistoryDate(item.createdAtUtc)}
                          {item.reportExportCount > 0 ? ` - ${item.reportExportCount} report${item.reportExportCount === 1 ? "" : "s"}` : ""}
                        </span>
                      </button>
                    );
                  })}
                  {!isHistoryLoading && runHistory.length === 0 && (
                    <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-500">
                      No saved screening runs yet.
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-zeos-orange-dk">
                  Screening Results
                </p>
                <h2 className="mt-1 text-xl font-semibold text-slate-950">{resultTitle}</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {liveRun
                    ? `${liveRun.status} - ${liveRun.currentStage || "Pending"}`
                    : "No screening has been run for this intake yet."}
                </p>
                {isRunning && pollCountdown > 0 && (
                  <span className="mt-2 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-zeos-orange-dk">
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-orange-200 border-t-zeos-orange-dk" />
                    Next update in {pollCountdown}s
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-md bg-zeos-orange-dk px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={openIncentiveMatching}
                  disabled={!canOpenIncentiveMatching}
                >
                  Go To Incentive Matching
                </button>
                {reportFormats.map((format) => (
                  <button
                    key={format.value}
                    type="button"
                    className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => exportReport(format.value)}
                    disabled={!canExportReport}
                    title={
                      isPreliminaryRun && !acknowledgedPreliminary
                        ? "Acknowledge the preliminary-results notice before exporting."
                        : undefined
                    }
                  >
                    {format.label}
                  </button>
                ))}
              </div>
            </div>

            {isPreliminaryRun && (
              <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p className="font-semibold">
                  Preliminary results — not a verified eligibility determination.
                </p>
                <p className="mt-1">
                  This run used Quick Screening. The status gate, eligibility verification, and
                  value stacking stages did not run, and no property record was attached, so
                  utility territory, ownership, affordability, and mixed-use allocation were not
                  applied. Program status has not been checked against any source. Re-run in{" "}
                  <span className="font-semibold">Existing Portfolio Property</span> mode for a
                  customer-ready result.
                </p>
                <label className="mt-2 flex items-start gap-2 font-semibold">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={acknowledgedPreliminary}
                    onChange={(event) => setAcknowledgedPreliminary(event.target.checked)}
                  />
                  <span>
                    I understand this report is preliminary and will not present it as verified
                    eligibility.
                  </span>
                </label>
              </div>
            )}

            {exportError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {exportError}
              </div>
            )}

            {!liveRun && (
              <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                Enter an address and run screening to check whether meaningful results can be generated.
              </div>
            )}

            {liveRun && (
              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-slate-950">
                      Live Agentic Run Output
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Review each screening step. Expand a step to see its result detail.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="self-start rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400"
                    onClick={() =>
                      setExpandedStageKeys(
                        allChecklistItemsExpanded ? [] : agenticChecklist.map((item) => item.key)
                      )
                    }
                  >
                    {allChecklistItemsExpanded ? "Collapse all" : "Expand all"}
                  </button>
                </div>

                <div className="mt-4 divide-y divide-slate-200 overflow-hidden rounded-md border border-slate-200 bg-white">
                  {agenticChecklist.map((item) => {
                    const isExpanded = expandedStageKeys.includes(item.key);
                    const tone = getChecklistStatusTone(item.status);
                    return (
                      <div key={item.key} className={tone.row}>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
                          onClick={() =>
                            setExpandedStageKeys((current) =>
                              current.includes(item.key)
                                ? current.filter((key) => key !== item.key)
                                : [...current, item.key]
                            )
                          }
                          aria-expanded={isExpanded}
                        >
                          <span className="flex min-w-0 items-center gap-3">
                            <span className={`h-2.5 w-2.5 flex-none rounded-full ${tone.dot}`} />
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-slate-950">
                                {item.label}
                              </span>
                              <span className="mt-0.5 block text-xs text-slate-500">
                                {item.hasDetail ? "Result detail available" : "Waiting for result detail"}
                              </span>
                            </span>
                          </span>
                          <span className="flex flex-none items-center gap-2">
                            <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${tone.badge}`}>
                              {item.status}
                            </span>
                            <span className="text-sm font-semibold text-slate-500">
                              {isExpanded ? "-" : "+"}
                            </span>
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="border-t border-slate-200 bg-white px-3 pb-3 pt-2">
                            <div className="whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                              {item.detail || "No result detail has been returned for this step yet."}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <DashboardFooter />
    </div>
  );
};
